<template>
  <div>
    <p class="text-2xl mb-1">
      Enriquecimento
    </p>
    <p class="mb-4 grey--text text--darken-1">
      Envie um arquivo em lote, acompanhe o status e baixe o retorno do enriquecimento.
    </p>

    <v-card>
      <v-tabs
        v-model="activeTab"
        background-color="transparent"
        color="primary"
      >
        <v-tab>Novo lote</v-tab>
        <v-tab>Lista de lotes</v-tab>
      </v-tabs>

      <v-tabs-items
        v-model="activeTab"
        class="enrichment-tabs-items"
      >
        <!-- Tab: Novo lote -->
        <v-tab-item>
          <v-card-text class="pt-6">
            <v-row>
              <v-col
                cols="12"
                md="6"
              >
                <v-select
                  v-model="idLayout"
                  :items="layoutOptions"
                  item-text="label"
                  item-value="value"
                  label="Layout"
                  outlined
                  dense
                  hide-details
                  :loading="loadingLayouts"
                />
                <p
                  v-if="selectedLayoutResumo"
                  class="mt-2 mb-0 caption grey--text text--darken-1"
                >
                  {{ selectedLayoutResumo }}
                </p>
              </v-col>
              <v-col
                cols="12"
                md="6"
              >
                <v-text-field
                  v-model="nomeArquivo"
                  label="Nome do arquivo"
                  outlined
                  dense
                  hide-details
                  placeholder="meu_lote.csv"
                />
              </v-col>

              <v-col cols="12">
                <input
                  ref="fileInput"
                  type="file"
                  accept=".txt,.csv,.xlsx,.xls"
                  hidden
                  @change="onFileChange"
                >
                <div
                  class="upload-dropzone"
                  :class="{
                    'upload-dropzone--dragover': isDragging,
                    'upload-dropzone--filled': Boolean(selectedFile),
                  }"
                  @click="openFileSelector"
                  @dragover.prevent="onDragOver"
                  @dragenter.prevent="onDragEnter"
                  @dragleave.prevent="onDragLeave"
                  @drop.prevent="onFileDrop"
                >
                  <template v-if="!selectedFile">
                    <v-icon
                      size="42"
                      color="grey"
                      class="mb-2"
                    >
                      {{ icons.mdiCloudUploadOutline }}
                    </v-icon>
                    <p class="upload-dropzone__title mb-1">
                      Arraste e solte o arquivo aqui
                    </p>
                    <p class="upload-dropzone__subtitle mb-0">
                      ou clique para selecionar (.txt, .csv, .xls, .xlsx)
                    </p>
                  </template>

                  <template v-else>
                    <div class="d-flex align-center justify-center flex-wrap">
                      <v-icon
                        color="success"
                        class="me-2"
                      >
                        {{ icons.mdiCheckCircle }}
                      </v-icon>
                      <div class="text-left me-2">
                        <p class="mb-0 font-weight-medium">
                          {{ selectedFile.name }}
                        </p>
                        <p class="mb-0 caption grey--text">
                          {{ formatBytes(selectedFile.size) }}
                        </p>
                      </div>
                      <v-btn
                        icon
                        small
                        color="error"
                        @click.stop="clearSelectedFile"
                      >
                        <v-icon small>
                          {{ icons.mdiClose }}
                        </v-icon>
                      </v-btn>
                    </div>
                    <p class="caption grey--text mb-0 mt-2">
                      Clique ou arraste outro arquivo para substituir
                    </p>
                  </template>
                </div>
              </v-col>
            </v-row>
          </v-card-text>

          <v-divider />

          <v-card-actions class="pa-4">
            <v-btn
              color="primary"
              class="me-3"
              :loading="uploading"
              :disabled="!canUpload"
              @click="upload"
            >
              Enviar lote
            </v-btn>
            <v-btn
              color="secondary"
              outlined
              @click="resetUpload"
            >
              Limpar
            </v-btn>
          </v-card-actions>
        </v-tab-item>

        <!-- Tab: Lista de lotes -->
        <v-tab-item>
          <v-card-title class="d-flex align-center justify-space-between flex-wrap px-4 pt-4 pb-4">
            <span>Lotes recentes</span>
            <div class="d-flex align-center mt-2 mt-sm-0">
              <v-text-field
                v-model.number="ultimosDias"
                label="Últimos dias"
                type="number"
                outlined
                dense
                hide-details
                style="max-width: 140px"
                class="me-2"
                min="1"
              />
              <v-btn
                color="primary"
                outlined
                :loading="loadingLotes"
                @click="loadLotes"
              >
                <v-icon
                  left
                  small
                >
                  {{ icons.mdiRefresh }}
                </v-icon>
                Atualizar
              </v-btn>
            </div>
          </v-card-title>

          <v-card-text>
            <v-alert
              v-if="lotesError"
              type="info"
              text
              class="mb-4"
            >
              {{ lotesError }}
            </v-alert>

            <v-data-table
              :headers="loteHeaders"
              :items="lotes"
              :loading="loadingLotes"
              :items-per-page="10"
              class="elevation-0"
              dense
              no-data-text="Nenhum lote encontrado"
            >
              <template #item.actions="{ item }">
                <v-btn
                  v-if="canApprove(item)"
                  small
                  text
                  color="success"
                  :loading="actionLoadingId === item.id && actionType === 'aprovar'"
                  @click="approve(item)"
                >
                  Aprovar
                </v-btn>
                <v-btn
                  small
                  text
                  color="primary"
                  :loading="actionLoadingId === item.id && actionType === 'retorno'"
                  @click="downloadRetorno(item)"
                >
                  Retorno
                </v-btn>
                <v-btn
                  small
                  text
                  @click="inspect(item)"
                >
                  Detalhes
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <v-dialog
      v-model="detailDialog"
      max-width="720"
    >
      <v-card>
        <v-card-title>Detalhes do lote</v-card-title>
        <v-card-text>
          <pre
            class="pa-3 grey lighten-4 rounded"
            style="max-height: 420px; overflow: auto; white-space: pre-wrap;"
          >{{ JSON.stringify(detailItem, null, 2) }}</pre>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="detailDialog = false"
          >
            Fechar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  mdiCloudUploadOutline,
  mdiRefresh,
  mdiCheckCircle,
  mdiClose,
} from '@mdi/js'
import {
  listarLayouts,
  listarLotes,
  uploadLote,
  aprovarLote,
  retornoLote,
  obterLote,
} from '@/services/enriquecimento'

function normalizeLotes(data) {
  const list =
    data?.lotes ||
    data?.Lotes ||
    data?.results ||
    (Array.isArray(data) ? data : [])

  if (!Array.isArray(list)) return []

  return list.map(item => ({
    id: item.IdLote || item.idLote || item.id || item.idlote,
    arquivo: item.NomeArquivo || item.nomearquivo || item.arquivo || '—',
    status:
      item.DescricaoStatus ||
      item.Status ||
      item.status ||
      item.IdStatusLote ||
      '—',
    layout: item.NomeLayout || item.IdLayout || item.idlayout || '—',
    data: item.DataInclusao || item.Data || item.data || '—',
    raw: item,
    idStatus: item.IdStatusLote || item.idStatusLote || item.statusId,
  }))
}

export default {
  data() {
    return {
      icons: {
        mdiCloudUploadOutline,
        mdiRefresh,
        mdiCheckCircle,
        mdiClose,
      },
      activeTab: 0,
      isDragging: false,
      layouts: [],
      loadingLayouts: false,
      idLayout: null,
      nomeArquivo: '',
      selectedFile: null,
      uploading: false,
      ultimosDias: 7,
      lotes: [],
      loadingLotes: false,
      lotesError: '',
      actionLoadingId: null,
      actionType: '',
      detailDialog: false,
      detailItem: null,
      loteHeaders: [
        { text: 'ID', value: 'id' },
        { text: 'Arquivo', value: 'arquivo' },
        { text: 'Status', value: 'status' },
        { text: 'Layout', value: 'layout' },
        { text: 'Data', value: 'data' },
        { text: 'Ações', value: 'actions', sortable: false },
      ],
    }
  },

  computed: {
    layoutOptions() {
      const seen = new Set()
      return (this.layouts || [])
        .filter(layout => {
          const id = layout.IdLayout
          if (seen.has(id)) return false
          seen.add(id)
          return true
        })
        .map(layout => ({
          label: `${layout.IdLayout} — ${layout.NomeLayout}`,
          value: layout.IdLayout,
          resumo: layout.Resumo,
        }))
    },

    selectedLayoutResumo() {
      const found = this.layoutOptions.find(item => item.value === this.idLayout)
      return found?.resumo || ''
    },

    canUpload() {
      return Boolean(this.idLayout && this.nomeArquivo && this.selectedFile)
    },
  },

  watch: {
    activeTab(tab) {
      if (tab === 1) {
        this.loadLotes()
      }
    },
  },

  async mounted() {
    await Promise.all([this.loadLayouts(), this.loadLotes()])
  },

  methods: {
    formatBytes(bytes) {
      if (!bytes && bytes !== 0) return ''
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    },

    openFileSelector() {
      this.$refs.fileInput?.click()
    },

    setSelectedFile(file) {
      if (!file) return
      this.selectedFile = file
      if (!this.nomeArquivo) {
        this.nomeArquivo = file.name
      }
    },

    onFileChange(event) {
      const file = event?.target?.files?.[0] || null
      this.setSelectedFile(file)
    },

    onDragOver() {
      this.isDragging = true
    },

    onDragEnter() {
      this.isDragging = true
    },

    onDragLeave(event) {
      if (event.currentTarget.contains(event.relatedTarget)) return
      this.isDragging = false
    },

    onFileDrop(event) {
      this.isDragging = false
      const file = event?.dataTransfer?.files?.[0] || null
      if (!file) return

      const allowed = /\.(txt|csv|xlsx|xls)$/i
      if (!allowed.test(file.name)) {
        this.$notify({
          type: 'error',
          text: 'Formato inválido. Use .txt, .csv, .xls ou .xlsx.',
        })
        return
      }

      this.setSelectedFile(file)
    },

    clearSelectedFile() {
      this.selectedFile = null
      if (this.$refs.fileInput) this.$refs.fileInput.value = null
    },

    resetUpload() {
      this.nomeArquivo = ''
      this.clearSelectedFile()
      this.isDragging = false
    },

    extractErrorMessage(error) {
      return (
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Falha na operação de enriquecimento.'
      )
    },

    async loadLayouts() {
      this.loadingLayouts = true
      try {
        const data = await listarLayouts()
        this.layouts = data?.layouts || data || []
        if (!this.idLayout && this.layoutOptions.length) {
          this.idLayout = this.layoutOptions[0].value
        }
      } catch (error) {
        this.$notify({
          type: 'error',
          text: this.extractErrorMessage(error),
        })
      } finally {
        this.loadingLayouts = false
      }
    },

    async loadLotes() {
      this.loadingLotes = true
      this.lotesError = ''
      try {
        const data = await listarLotes(this.ultimosDias || 7)
        this.lotes = normalizeLotes(data)
      } catch (error) {
        const message = this.extractErrorMessage(error)
        // Lista vazia não é erro — só limpa a tabela
        if (/não foi encontrado nenhum lote/i.test(String(message))) {
          this.lotes = []
          this.lotesError = ''
        } else {
          this.lotesError = message
          this.lotes = []
        }
      } finally {
        this.loadingLotes = false
      }
    },

    async upload() {
      if (!this.canUpload) {
        this.$notify({
          type: 'error',
          text: 'Selecione layout, nome e arquivo.',
        })
        return
      }

      this.uploading = true
      this.$loading(true)
      try {
        await uploadLote({
          file: this.selectedFile,
          nomearquivo: this.nomeArquivo,
          idlayout: this.idLayout,
        })
        this.$notify({
          type: 'success',
          text: 'Lote enviado com sucesso.',
        })
        this.resetUpload()
        await this.loadLotes()
        this.activeTab = 1
      } catch (error) {
        this.$notify({
          type: 'error',
          text: this.extractErrorMessage(error),
        })
      } finally {
        this.uploading = false
        this.$loading(false)
      }
    },

    canApprove(item) {
      const status = String(item.status || '').toLowerCase()
      const idStatus = Number(item.idStatus)
      return idStatus === 3 || status.includes('aguardando')
    },

    async approve(item) {
      this.actionLoadingId = item.id
      this.actionType = 'aprovar'
      this.$loading(true)
      try {
        await aprovarLote(item.id, { retornos: 1 })
        this.$notify({ type: 'success', text: 'Lote aprovado.' })
        await this.loadLotes()
      } catch (error) {
        this.$notify({
          type: 'error',
          text: this.extractErrorMessage(error),
        })
      } finally {
        this.actionLoadingId = null
        this.actionType = ''
        this.$loading(false)
      }
    },

    async downloadRetorno(item) {
      this.actionLoadingId = item.id
      this.actionType = 'retorno'
      this.$loading(true)
      try {
        const data = await retornoLote(item.id)
        const content =
          typeof data === 'string' ? data : JSON.stringify(data, null, 2)
        const blob = new Blob([content], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `retorno-lote-${item.id}.json`
        link.click()
        URL.revokeObjectURL(url)
        this.$notify({ type: 'success', text: 'Retorno baixado.' })
      } catch (error) {
        this.$notify({
          type: 'error',
          text: this.extractErrorMessage(error),
        })
      } finally {
        this.actionLoadingId = null
        this.actionType = ''
        this.$loading(false)
      }
    },

    async inspect(item) {
      this.$loading(true)
      try {
        const data = await obterLote(item.id)
        this.detailItem = data
        this.detailDialog = true
      } catch (error) {
        this.detailItem = item.raw || item
        this.detailDialog = true
        this.$notify({
          type: 'warning',
          text: this.extractErrorMessage(error),
        })
      } finally {
        this.$loading(false)
      }
    },
  },
}
</script>

<style scoped>
.enrichment-tabs-items {
  background-color: transparent !important;
}

.enrichment-tabs-items >>> .v-window__container,
.enrichment-tabs-items >>> .v-window-item {
  background-color: transparent !important;
}

.upload-dropzone {
  border: 2px dashed rgba(0, 0, 0, 0.22);
  border-radius: 8px;
  padding: 36px 20px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  background-color: rgba(0, 0, 0, 0.02);
  min-height: 160px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.theme--dark .upload-dropzone {
  border-color: rgba(255, 255, 255, 0.28);
  background-color: rgba(255, 255, 255, 0.04);
}

.upload-dropzone:hover,
.upload-dropzone--dragover {
  border-color: var(--v-primary-base, #9155fd);
  background-color: rgba(145, 85, 253, 0.08);
}

.upload-dropzone--filled {
  border-style: solid;
  border-color: rgba(76, 175, 80, 0.5);
  background-color: rgba(76, 175, 80, 0.08);
}

.upload-dropzone__title {
  font-size: 16px;
  font-weight: 500;
}

.upload-dropzone__subtitle {
  font-size: 13px;
  opacity: 0.75;
}
</style>
